import { Menu, MenuItem } from '@szhsin/react-menu';
import styled from 'styled-components';

import { ModelInfo } from '#/api/terminal/types';

interface Props {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

export default function ModelPicker({ models, selectedId, onSelect }: Props) {
  if (models.length === 0) return null;
  const selected = models.find((m) => m.id === selectedId) ?? models[0];
  if (selected == null) return null;
  return (
    <Menu
      menuButton={
        <Trigger type="button" aria-label={`Model: ${selected.label}`}>
          <TriggerLabel>{selected.label}</TriggerLabel>
        </Trigger>
      }
      align="end"
      direction="top"
      transition
      menuClassName="terminal-model-picker-menu"
    >
      <Heading>Models</Heading>
      {models.map((m) => (
        <MenuItem
          key={m.id}
          onClick={() => onSelect(m.id)}
          className="terminal-model-picker-item"
        >
          <ItemRow>
            <ItemLabel>
              {m.label}
              {m.legacy === true && <LegacyTag>Legacy</LegacyTag>}
            </ItemLabel>
            <CheckSlot>{m.id === selected.id && <CheckIcon />}</CheckSlot>
          </ItemRow>
        </MenuItem>
      ))}
    </Menu>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12L10 17L20 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const Trigger = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    color: var(--text-primary);
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

const Heading = styled.div`
  padding: 6px 10px 8px;
  font-size: 0.7rem;
  color: var(--text-secondary);
`;

const ItemRow = styled.span`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegacyTag = styled.span`
  font-size: 0.7rem;
  color: var(--text-secondary);
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;
